import {useCompanyEstimatedBillingPrice} from '~/api/deimos/estimate-billing-price'
import type {PlanResponse} from '~/api/types/billing'
import {BillingInterval, Interval} from '~/api/types/billing'
import type {PlanType} from '~/api/types/plan'
import {getFeatureCountryConfiguration} from '~/country-configuration/configuration'
import {Feature} from '~/country-configuration/types'
import type Country from '~/locale/country'
import {CurrencyFormat, formatCurrency} from '~/locale/currency'
import {useUser} from '~/shell/user'

export const findPlan = (planType: PlanType, planInterval: Interval, plans?: PlanResponse[]) =>
    plans?.find(
        ({type, interval, intervalCount}) =>
            type === planType && interval && interval === planInterval && intervalCount === 1,
    )

export const toStringNoDecimal = (amount: number | undefined, currency: string): string =>
    formatCurrency(amount, currency, {format: CurrencyFormat.FixedFractionDigits(0)})

export const isPayingAnnually = (
    commitmentInterval?: Interval,
    billingInterval?: BillingInterval,
    isCashbackCountry?: boolean,
): boolean => {
    if (isCashbackCountry) {
        return billingInterval === BillingInterval.YEARLY
    }

    return commitmentInterval === Interval.YEAR
}

export interface PricePlanPrices {
    monthly: number
    monthlyDiscounted: number
    yearly: number
    yearlyDiscounted: number
}
export interface PricePlanDetails {
    prices: PricePlanPrices
    currency: string | undefined
}

/**
 * Prices for annual billing are set up in Stripe (for each country) as fixed amounts,
 * with the discount included. To calculate the monthly prices and discounted prices, we
 * use multiply/divide using the month/year prices.
 *
 * For annual commitment with yearly billing we are only using monthly prices
 * as a visual representation
 */

export const getPricePlanDetails = (
    planType: PlanType,
    plans?: PlanResponse[],
): PricePlanDetails => {
    const monthlyPlan = findPlan(planType, Interval.MONTH, plans)
    const yearlyPlan = findPlan(planType, Interval.YEAR, plans)

    if (!monthlyPlan && !yearlyPlan) {
        throw new Error(`At least one of monthly or yearly plan must be available for ${planType}`)
    }

    const monthly = monthlyPlan?.amountDecimal || 0
    const yearly = monthly * 12
    const yearlyDiscounted = yearlyPlan?.amountDecimal || 0
    const monthlyDiscounted = yearlyDiscounted / 12

    const currency = monthlyPlan?.currency

    return {
        prices: {
            monthly,
            monthlyDiscounted,
            yearly,
            yearlyDiscounted,
        },
        currency,
    }
}

export const usePricingBreakDown = (
    planType: PlanType,
    planInterval: Interval,
    billingInterval: BillingInterval,
    country?: Country,
) => {
    const user = useUser()
    const {isCashBackCountry} = getFeatureCountryConfiguration(Feature.Paywall, country)
    const includeCashback = isCashBackCountry && planInterval === Interval.YEAR
    const monthlyPriceEstimateResponse = useCompanyEstimatedBillingPrice(user?.companyId ?? null, {
        numberOfUsers: user?.company?.numberOfUsers || 0,
        startDate: new Date(),
        planType,
        intervalType: 'MONTHLY',
        intervalCommitment: 1,
        contract: false,
        includeCashback: Boolean(includeCashback),
    })
    const yearlyPriceEstimateResponse = useCompanyEstimatedBillingPrice(user?.companyId ?? null, {
        numberOfUsers: user?.company?.numberOfUsers || 0,
        startDate: new Date(),
        planType,
        intervalType: 'YEARLY',
        intervalCommitment: 1,
        contract: false,
        includeCashback: Boolean(includeCashback),
    })
    const monthlyPriceWithoutDiscount = monthlyPriceEstimateResponse.data?.subtotal?.value || 0
    const yearlyPriceWithoutDiscount = monthlyPriceWithoutDiscount * 12
    const yearlyPriceWithDiscount = yearlyPriceEstimateResponse.data?.subtotal?.value || 0
    const monthlyPriceWithDiscount = yearlyPriceWithDiscount / 12
    const currency = monthlyPriceEstimateResponse.data?.subtotal?.currency
    const hasDiscount =
        planInterval === Interval.YEAR &&
        (!isCashBackCountry || (isCashBackCountry && billingInterval === BillingInterval.YEARLY)) &&
        yearlyPriceWithDiscount < yearlyPriceWithoutDiscount
    return {
        isValidating:
            monthlyPriceEstimateResponse.isValidating || yearlyPriceEstimateResponse.isValidating,
        currency,
        hasDiscount,
        monthlyPrice: {
            standardFormatted: formatCurrency(monthlyPriceWithoutDiscount, currency),
            standard: monthlyPriceWithoutDiscount,
            discountedFormatted: hasDiscount
                ? formatCurrency(monthlyPriceWithDiscount, currency)
                : undefined,
            discounted: hasDiscount ? monthlyPriceWithDiscount : undefined,
        },
        yearlyPrice: {
            standardFormatted: formatCurrency(yearlyPriceWithoutDiscount, currency),
            standard: yearlyPriceWithoutDiscount,
            discountedFormatted: hasDiscount
                ? formatCurrency(yearlyPriceWithDiscount, currency)
                : undefined,
            discounted: hasDiscount ? yearlyPriceWithDiscount : undefined,
        },
    }
}
